"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { GET, POST, PUT, PATCH, DELETE } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing node touch related API-I endpoints
 * @class
 */
class NodeTouch {
    constructor(api) {
        this.api = api;
    }
    /**
     * Drop install a Node according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-drop-node-post}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier being installed
     * @param {!string} args.nodeType - Required. The type of node being installed
     * @param {!string} args.location - Required. WKT of the lat/lng of the node
     * @param {!number} args.propertyId - Required. Which property to assign this node to
     * @param {!string} args.managementType -  Required. Management type of the new node: commercial | research | defunct | test
     * @param {!number} args.maintenanceOwnerId - Required. The id of maintenance owner provider
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?string} args.force - Optional. Set to handle node identifier conflicts: replan | remove
     * @param {?string} args.name - Optional. The name of the new node
     * @param {?number} args.pseudogatewayId - Optional. The id of the pseudogateway. SN only.
     * @param {?string} args.gatewayMac - Optional. SNAP mac address of the gateway. SN only.
     * @param {?number} args.gpsAccuracy - Optional. The gps accuracy recorded when installing this node
     * @param {?string} args.antennaType - Optional. Sets the antenna type, DN only: DEFAULT | DIRECTIONAL | OMNIDIRECTIONAL
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @param {?object} args.dispenser={} - Optional. An object containing the dispenser arguments
     * @param {?number} args.dispenser.revision - Optional. The revision of the node. SN_SCD only
     * @param {?number} args.dispenser.formulationId - Optional. The formulation id of a formulation can installed with the node. SCD only
     * @param {?number} args.dispenser.formulationBatchId - Optional. The formulation batch id of a formulation can installed with the node. SCD only
     * @param {?object} args.trap={} - Optional. An object containing the trap arguments
     * @param {?number} args.trap.pestTypeId - Optional. Target pest type for the trap
     * @param {?number[]} args.trap.lureIds - Optional. Installed lure ids
     * @param {?boolean} args.trap.activeLures - Optional. Active lures
     * @param {?string} args.trap.trapCameraOrientation - Optional. Sets the camera orientation value of the trap if different to that of the primary lure: HORIZONTAL | VERTICAL
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async installNodeFromDrop(args) {
        const { nodeIdentifier, nodeType, location, propertyId, managementType, maintenanceOwnerId, customerId, force, name, pseudogatewayId, gatewayMac, gpsAccuracy, antennaType, imageUrls, dispenser: { revision, formulationId, formulationBatchId } = {}, trap: { pestTypeId, lureIds, activeLures, trapCameraOrientation } = {}, } = args;
        const methodName = 'installNodeFromDrop';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'nodeType', nodeType, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeType', nodeType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'force', force, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'name', name, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'pseudogatewayId', pseudogatewayId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'gatewayMac', gatewayMac, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'gpsAccuracy', gpsAccuracy, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'antennaType', antennaType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'revision', revision, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'formulationId', formulationId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'formulationBatchId', formulationBatchId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'pestTypeId', pestTypeId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'lureIds', lureIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'activeLures', activeLures, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'trapCameraOrientation', trapCameraOrientation, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = '/nodes/common/drop';
        const input = {
            header: {
                customerId,
            },
            query: {
                force,
            },
            body: {
                nodeIdentifier,
                nodeType: nodeType.toUpperCase(),
                location,
                propertyId,
                managementType,
                maintenanceOwnerId,
                name,
                pseudogatewayId,
                gatewayMac,
                gpsAccuracy,
                antennaType,
                imageUrls,
                dispenser: {
                    revision,
                    formulationId,
                    formulationBatchId,
                },
                trap: {
                    pestTypeId,
                    lureIds,
                    activeLures,
                    trapCameraOrientation,
                },
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(POST, route, endPointInput);
    }
    /**
     * Reposition a Node according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-reposition-node-put}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier being installed
     * @param {!string} args.location - Required. WKT of the lat/lng of the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?boolean} args.moveLogs - Required. Whether the user would like to move all previous logs to the new location
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return void or an error with the problem.
     */
    async repositionNode(args) {
        const { nodeIdentifier, location, customerId, moveLogs, imageUrls } = args;
        const methodName = 'repositionNode';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'moveLogs', moveLogs, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/reposition`;
        const input = {
            header: {
                customerId,
            },
            body: {
                location,
                moveLogs,
                imageUrls,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
    /**
     * Remove a Node permanently according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-destroy-node-delete}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier being installed
     * @param {!string} args.reason - Required. The reason to remove this node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @return {promise} Returns a Promise that, when fulfilled, will either return void or an error with the problem.
     */
    async removeNode(args) {
        const { nodeIdentifier, customerId, reason, imageUrls } = args || {};
        const methodName = 'removeNode';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/remove_permanent`;
        const input = {
            header: {
                customerId,
            },
            body: {
                reason,
                imageUrls,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(DELETE, route, endPointInput);
    }
    /**
     * Swap a Node according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-reposition-node-put}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifierToRemove - Required. The node identifier being swapped out
     * @param {!string} args.nodeIdentifierToInstall - Required. The node identifier being swapped in
     * @param {!string} args.reason - Required. The reason to swap this node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?string} args.reasonDescription - Optional. The description of `reason` key above. Required only when `OTHER` is selected as `reason`
     * @param {?string} args.force - Optional.  Optional. Set to handle node identifier conflicts: replan | remove
     * @param {?string} args.newName - Optional. New name for node
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @param {?object} args.dispenser={} - Optional. An object containing the dispenser arguments
     * @param {?number} args.dispenser.revision - Optional. The revision of the node. SN_SCD only
     * @param {?number} args.dispenser.canSwapped = Optional. When the can of existing formulation is swapped with the swapped in node. SCD only.
     * @param {?number} args.dispenser.formulationId - Optional. The formulation id of a formulation can installed with the node. SCD only
     * @param {?number} args.dispenser.formulationBatchId - Optional. The formulation batch id of a formulation can installed with the node. SCD only
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async swapNode(args) {
        const { nodeIdentifierToRemove, nodeIdentifierToInstall, reason, customerId, reasonDescription, force, newName, imageUrls, dispenser: { revision, canSwapped, formulationId, formulationBatchId } = {}, } = args;
        const methodName = 'swapNode';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifierToRemove', nodeIdentifierToRemove, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifierToRemove', nodeIdentifierToRemove, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'nodeIdentifierToInstall', nodeIdentifierToInstall, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifierToInstall', nodeIdentifierToInstall, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'reasonDescription', reasonDescription, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'force', force, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'newName', newName, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'revision', revision, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'canSwapped', canSwapped, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'formulationId', formulationId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'formulationBatchId', formulationBatchId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifierToRemove}/swap/${nodeIdentifierToInstall}`;
        const input = {
            header: {
                customerId,
            },
            body: {
                reason,
                reasonDescription,
                force,
                newName,
                imageUrls,
                dispenser: {
                    revision,
                    canSwapped,
                    formulationId,
                    formulationBatchId,
                },
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
    /**
     * Get Swap Node Reasons
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-get-swap-reasons}
     * @return {promise} Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async getSwapNodeReasons() {
        const route = '/nodes/common/swap/reasons';
        return this.api.send(GET, route);
    }
    /**
     * Get Remove Node Reasons
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-core-get-node-removal-reasons}
     * @return {promise} Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async getRemoveNodeReasons() {
        const route = '/nodes/common/remove/reasons';
        return this.api.send(GET, route);
    }
    /**
     * Get multiple Nodes Config
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-core-get-node-config-details-get}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string[]} args.nodeIdentifiers - Required. List of node identifiers to query
     * @return {promise} Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async getNodesConfig(args) {
        const { nodeIdentifiers } = args || {};
        const methodName = 'getNodesConfig';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifiers.join(',')}/config`;
        const input = {
            body: null,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, endPointInput);
    }
    /**
     * Update Node images
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-core-set-node-images-patch}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.nodeIdentifier - Required. The node identifier whose images are being updated
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.nodeAction - Required. Node touch
     * @param {!number} args.utcDate - Required. Image date (UTC)
     * @param {!string[]} args.imageUrls - Required. An array of image urls for the node
     * @return {promise} Returns a Promise that, when fulfilled, will either return void or an error with the problem.
     */
    async setNodeImages(args) {
        const { nodeIdentifier, customerId, nodeAction, utcDate, imageUrls } = args;
        const methodName = 'setNodeImages';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'nodeAction', nodeAction, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeAction', nodeAction, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'utcDate', utcDate, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'utcDate', utcDate, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/common/${nodeIdentifier}/image`;
        const input = {
            header: {
                customerId,
            },
            body: {
                nodeAction,
                utcDate,
                imageUrls,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PATCH, route, endPointInput);
    }
    /**
     * Set Insect on TRP Nodes according to the parameters provided
     *
     * Set Insect on TRP node according to the parameters provided
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-traps-set-targeted-insect-on-trap-put|API Call}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string[]} args.nodeIdentifiers - Required. The node identifiers to be set with the insect
     * @param {!number} args.insectId - Required. The insect id to set on the nodes
     * @param {!number} args.customerId - Required. The id of customer
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async setInsect(args) {
        const { nodeIdentifiers, insectId, customerId } = args;
        const methodName = 'setInsect';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifiers, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'insectId', insectId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'insectId', insectId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/trp/pest/${insectId}`;
        const input = {
            header: {
                customerId,
            },
            body: {
                nodeIdentifiers,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
    /**
     * Remove trap node targete insect
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-traps-remove-pest-type-on-trap-delete|API Call}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string[]} args.nodeIdentifiers - Required. The node identifiers to be removed from the insect
     * @param {!number} args.customerId - Required. The id of customer
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async removeInsect(args) {
        const { nodeIdentifiers, customerId } = args;
        const methodName = 'removeInsect';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifiers, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = '/nodes/trp/pest';
        const input = {
            header: {
                customerId,
            },
            body: {
                nodeIdentifiers,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(DELETE, route, endPointInput);
    }
    /**
     * Reposition a node according to the parameters provided
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-traps-set-camera-settings-on-traps-put|API Call}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string[]} args.nodeIdentifiers - Required. The node identifiers to be set for camera orientation
     * @param {!string} args.cameraOrientationToSet - Required. The camera orientation
     * @param {!number} args.customerId - Required. The id of customer
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async setCameraOrientation(args) {
        const { nodeIdentifiers: nodeIdentifiersToSet, cameraOrientationToSet, customerId } = args;
        const methodName = 'setCameraOrientation';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifiersToSet', nodeIdentifiersToSet, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifiersToSet', nodeIdentifiersToSet, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'cameraOrientationToSet', cameraOrientationToSet, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'cameraOrientationToSet', cameraOrientationToSet, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = '/nodes/trp/camera_settings';
        const input = {
            header: {
                customerId,
            },
            body: {
                nodeIdentifiersToSet,
                cameraOrientationToSet,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
    /**
     * Reposition a node according to the parameters provided
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-core-update-node-config-details-put|API Call}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string[]} args.nodeIdentifiers - Required. The node identifiers to be set.
     * @param {!string} args.nodeType - Required. The node type to be set.
     * @param {?string} [args.name] - Optional. The name of the node.
     * @param {?number} [args.propertyId] - Optional. ID of the property owning this node
     * @param {?string} [args.gatewayMac] - Optional. The gateway MAC address.
     * @param {?number} [args.pseudoGatewayId] - Optional. The pseudogateway ID.
     * @param {?string} [args.desiredFw] - Optional. The desired firmware version.
     * @param {?number} [args.revision] - Optional. The revision number of the node.
     * @param {?string} [args.managementType] - Optional. The management type of the node.
     * @param {?number} [args.maintenanceOwnerId] - Optional. The maintenance owner ID.
     * @param {?number} [args.assetOwnerId] - Optional. The asset owner ID.
     * @param {?boolean} [args.hiddenFromCustomer] - Optional. Whether the node is hidden from customers.
     * @param {?string} [args.antennaType] - Optional. The type of antenna used by the node.
     * @param {!number} args.customerId - Required. The id of customer
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async updateNodeConfig(args) {
        const { nodeIdentifiers, nodeType, gatewayMac, pseudoGatewayId, desiredFw, revision, managementType, maintenanceOwnerId, assetOwnerId, hiddenFromCustomer, antennaType, customerId, } = args;
        const methodName = 'setCameraOrientation';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifiers', nodeIdentifiers, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'nodeType', nodeType, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeType', nodeType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'gatewayMac', gatewayMac, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'pseudoGatewayId', pseudoGatewayId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'desiredFw', desiredFw, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'revision', revision, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'assetOwnerId', assetOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'hiddenFromCustomer', hiddenFromCustomer, util_1.ValidationTypes.BOOLEAN),
            (0, util_1.validateParam)(methodName, 'antennaType', antennaType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/${nodeType}/config`;
        const input = {
            header: {
                customerId,
            },
            body: args,
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
}
exports.default = NodeTouch;
//# sourceMappingURL=node-touch.js.map